import type { TaskWithTags, Tag, CreateTaskInput, UpdateTaskInput, ListFilters } from './types.ts';
import { dataSourceFromHeaders } from './offline-core.ts';

const API_BASE = '/api';

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  let res: Response;
  try {
    res = await fetch(API_BASE + path, opts);
  } catch (_err) {
    // Sieć padła zanim doszło do odpowiedzi (zapisów service worker nie przechwytuje — spec/pwa.md).
    throw new Error(method === 'GET' ? 'Brak połączenia z serwerem' : 'Brak połączenia — zmiana nie została zapisana');
  }
  // Offline read (spec/pwa.md → Etap 2): odpowiedź z cache SW niesie X-From-Cache — aplikacja pokazuje pasek.
  if (method === 'GET') {
    window.dispatchEvent(new CustomEvent('tmk:data-source', { detail: dataSourceFromHeaders(res.headers) }));
  }
  if (res.status === 401) {
    window.location.reload();
    throw new Error('Not authenticated');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

export const TasksAPI = {
  list: (filters: ListFilters = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    }
    const qs = params.toString();
    return request<TaskWithTags[]>('GET', '/tasks' + (qs ? '?' + qs : ''));
  },
  getById: (id: string) => request<TaskWithTags>('GET', `/tasks/${id}`),
  create: (data: CreateTaskInput) => request<TaskWithTags>('POST', '/tasks', data),
  update: (id: string, data: UpdateTaskInput) => request<TaskWithTags>('PUT', `/tasks/${id}`, data),
  delete: (id: string) => request<{ ok: boolean }>('DELETE', `/tasks/${id}`),
  move: (id: string, beforeId: string | null, parentId?: string | null) =>
    request<TaskWithTags>('PUT', `/tasks/${id}/move`, { before_id: beforeId, parent_id: parentId }),
  /** direction 1 (indent) z `parentId` = WIDOCZNE rodzeństwo powyżej (spec: Przyciski przesuwania → element powyżej). */
  nest: (id: string, direction: -1 | 1, parentId?: string | null) =>
    request<TaskWithTags>('PUT', `/tasks/${id}/nest`, parentId ? { direction, parent_id: parentId } : { direction }),
  /** Cele dla „Przenieś do…”: szukanie po stronie serwera, bez zaznaczonych i ich poddrzew (spec/zadania.md). */
  searchTargets: (q: string, excludeIds: string[], limit = 15) =>
    request<{ id: string; title: string; path: string[] }[]>('GET',
      '/tasks/search?' + new URLSearchParams({ q, exclude: excludeIds.join(','), limit: String(limit) }).toString()),
  ancestors: (id: string) =>
    request<{ id: string; title: string }[]>('GET', `/tasks/${id}/ancestors`),
  batch: (ids: string[], action: string, payload: Record<string, unknown> = {}) =>
    request<{ updated: number; skipped?: number }>('POST', '/tasks/batch', { ids, action, payload }),
};

export const TagsAPI = {
  list: () => request<Tag[]>('GET', '/tags'),
  create: (data: { name: string; color: string; icon?: string | null }) => request<Tag>('POST', '/tags', data),
  update: (id: string, data: { name?: string; color?: string; icon?: string | null }) => request<Tag>('PUT', `/tags/${id}`, data),
  delete: (id: string) => request<{ ok: boolean }>('DELETE', `/tags/${id}`),
};

export interface SavedView {
  id: string;
  name: string;
  params: Record<string, unknown>;
  position: number;
}

export interface Attachment {
  id: string;
  task_id: string;
  filename: string;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  author_email: string;
  created_at: string;
}

export const AttachmentsAPI = {
  list: (taskId: string) => request<Attachment[]>('GET', `/attachments/${taskId}`),
  upload: async (taskId: string, file: File): Promise<Attachment> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/attachments/${taskId}`, {
      method: 'POST',
      credentials: 'same-origin',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || res.statusText);
    }
    return res.json();
  },
  delete: (id: string) => request<{ ok: boolean }>('DELETE', `/attachments/${id}`),
  fileUrl: (filename: string) => `/api/attachments/file/${filename}`,
};

export const ViewsAPI = {
  list: () => request<SavedView[]>('GET', '/views'),
  create: (data: { name: string; params: Record<string, unknown> }) => request<SavedView>('POST', '/views', data),
  update: (id: string, data: { name: string }) => request<SavedView>('PUT', `/views/${id}`, data),
  delete: (id: string) => request<{ ok: boolean }>('DELETE', `/views/${id}`),
};

// Statystyki (spec/statystyki.md)
export type StatsBucket = 'day' | 'week' | 'month';
export interface StatsRow { period: string; created: number; completed: number; cancelled: number; deleted: number }
export interface StatsResult {
  bucket: StatsBucket;
  from: string;
  to: string;
  rows: StatsRow[];
  totals: { created: number; completed: number; cancelled: number; deleted: number };
}
export const StatsAPI = {
  summary: (p: { from?: string; to?: string; bucket?: StatsBucket; tag_groups?: string; tag_exclude?: string }) => {
    const qs = new URLSearchParams();
    if (p.from) qs.set('from', p.from);
    if (p.to) qs.set('to', p.to);
    if (p.bucket) qs.set('bucket', p.bucket);
    if (p.tag_groups) qs.set('tag_groups', p.tag_groups);
    if (p.tag_exclude) qs.set('tag_exclude', p.tag_exclude);
    return request<StatsResult>('GET', '/stats?' + qs.toString());
  },
};
